import React, { memo } from 'react'
import PropTypes from 'prop-types'

/**
 * RiskSummaryCards - Displays 4 summary metric cards
 *
 * @param {Object} summary - Summary data from report
 */
const RiskSummaryCards = memo(({ summary }) => {
	if (!summary) {
		return null
	}

	const {
		total_projects = 0,
		projects_with_risk_checklists = 0,
		projects_without_risk_checklists = 0,
		overall_risk_level = 'Unknown',
		overall_risk_score = 0
	} = summary

	const getRiskLevelClass = (level) => {
		const levelLower = level?.toLowerCase()
		if (levelLower === 'high') return 'risk-high'
		if (levelLower === 'medium') return 'risk-medium'
		if (levelLower === 'low') return 'risk-low'
		return 'risk-unknown'
	}

	return (
		<div className="risk-summary-cards">
			<div className="risk-summary-card">
				<div className="risk-summary-card__icon risk-summary-card__icon--primary">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
					</svg>
				</div>
				<div className="risk-summary-card__content">
					<span className="risk-summary-card__label">Total Projects</span>
					<span className="risk-summary-card__value">{total_projects}</span>
				</div>
			</div>

			<div className="risk-summary-card">
				<div className="risk-summary-card__icon risk-summary-card__icon--success">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
				</div>
				<div className="risk-summary-card__content">
					<span className="risk-summary-card__label">With Risk Checklists</span>
					<span className="risk-summary-card__value">{projects_with_risk_checklists}</span>
				</div>
			</div>

			<div className="risk-summary-card">
				<div className="risk-summary-card__icon risk-summary-card__icon--warning">
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
					</svg>
				</div>
				<div className="risk-summary-card__content">
					<span className="risk-summary-card__label">Without Risk Checklists</span>
					<span className="risk-summary-card__value">{projects_without_risk_checklists}</span>
				</div>
			</div>

			<div className="risk-summary-card">
				<div className={`risk-summary-card__icon risk-summary-card__icon--${getRiskLevelClass(overall_risk_level)}`}>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
						<path d="M13 10V3L4 14h7v7l9-11h-7z" />
					</svg>
				</div>
				<div className="risk-summary-card__content">
					<span className="risk-summary-card__label">Overall Risk</span>
					<span className={`risk-summary-card__value risk-summary-card__value--${getRiskLevelClass(overall_risk_level)}`}>
						{overall_risk_level}
						{overall_risk_score > 0 && (
							<span className="risk-summary-card__score">({overall_risk_score})</span>
						)}
					</span>
				</div>
			</div>
		</div>
	)
})

RiskSummaryCards.displayName = 'RiskSummaryCards'

RiskSummaryCards.propTypes = {
	summary: PropTypes.shape({
		total_projects: PropTypes.number,
		projects_with_risk_checklists: PropTypes.number,
		projects_without_risk_checklists: PropTypes.number,
		overall_risk_level: PropTypes.string,
		overall_risk_score: PropTypes.number
	})
}

export default RiskSummaryCards
